import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { map, Observable } from 'rxjs';
import { environment } from '../../../../environments/environment';

@Injectable({
  providedIn: 'root'
})
export class TypeaheadService {
  private baseUrl: string;  // Base API URL from environment config

  constructor(private http: HttpClient) {
    this.baseUrl = environment.api.url;  // Set base URL from environment
  }

  /**
   * Generic API method for performing ASMX-based typeahead searches.
   *
   * @param serviceName - ASMX service name (e.g., "SearchCharge.asmx")
   * @param methodName - Web method to call on service (e.g., "SearchByPdNumberNoJQ")
   * @param payload - Search payload containing prefixText, count, and contextKey
   * @returns Observable<ITypeaheadOption[]> emitting array of options parsed from response.
   *
   * - Sends HTTP POST to ASMX endpoint constructed from baseUrl + service/method.
   * - Expects response as an array of JSON strings.
   * - Parses each string safely inside try-catch.
   * - Logs and filters out invalid JSON strings.
   */
  public search(
    serviceName: string,                          // ASMX service name
    methodName: string,                           // Web method name
    payload: ITypeaheadSearch_Request,            // Search criteria
    formatterFn?: (item: any) => ITypeaheadOption // Formatter for the typeahed
  ): Observable<ITypeaheadOption[]> {
    // Construct full url for the ASMX web method call
    const url = `${this.baseUrl}WebServices/${serviceName}/${methodName}`;

    // POST request to API, expecting a string array response
    return this.http.post<string[]>(url, payload).pipe(
      map(res => {
        if (!res?.length) {
          console.warn('TypeaheadService.search: empty response received.');
          return [];
        }

        // Parse each JSON string into an ITypeaheadOption object
        return res.map(jsonStr => {
          try {
            const parsed = <ISearchResponse>JSON.parse(jsonStr); // Expected shape: { First: string, Second: string }
            const formatter = formatterFn ?? defaultFormatter;

            return formatter(parsed);
          } catch (err) {
            console.error('TypeaheadService.search: Invalid JSON string in response:', jsonStr);
            return null;  // Returning null for invalid entries, filtered below
          }
        })
          .filter(Boolean) as ITypeaheadOption[];  // Remove null entries due to parse errors
      })
    );
  }

  /**
   * Performs backend validation to check if a value exists using ValidatorWrapper.
   *
   * @param serviceName - ASMX wrapper service, typically "ValidatorWrapper,App_Code.ashx"
   * @param methodName - Method to call, typically "IsExist"
   * @param payload - Object containing validation input
   * @returns Observable<boolean> indicating if the value is valid
   */
  public validate(
    serviceName: string,
    methodName: ValidationMethod,
    payload: ITypeaheadValidation_Request | IGetValueRequest | IIsTableHasRecordRequest | ITypeAheadIsExistWhereRequest
  ): Observable<string> {
    const url = `${this.baseUrl}WebServices/${serviceName}/${getValidationMethodName(methodName)}`;

    return this.http.post<string>(url, payload, { params: { 'showLoader': true } }).pipe(
      map(response => {
        try {
          return response;
        } catch (e) {
          console.error('[TypeaheadService] Failed to parse validation response', response, e);
          return '';
        }
      })
    );
  }
}

export const defaultFormatter = (item: ISearchResponse): ITypeaheadOption => {
  return {
    DisplayText: item?.First ?? '',
    Value: item?.Second ?? ''
  };
}

//#region Interfaces
export interface ISearchResponse {
  First: string;
  Second: string;
}

export interface ITypeaheadSearch_Request {
  prefixText: string;  // Text user typed for filtering on server
  count: number;       // Maximum number of options to return
  contextKey: string | null;  // Contextual filtering key e.g., user or company context
}

export interface ITypeaheadOption {
  DisplayText: string; // Text to display in dropdown options
  Value: string;       // Underlying value of the option (id, key, etc.)
  DisplayTextInSuggestion?: string;
}

export interface ITypeaheadValidation_Request {
  strTableName: string;
  strColumnName: string;
  strColumnValue?: string,
  strWhereColumnName: string;
  strWhereColumnValue: string;
  errorMessage?: string;           // Optional custom error message  
  bUseQuote: boolean;
  bUseQuoteForWhere: boolean;
}

export interface ITypeaheadValidationConfig extends ITypeaheadValidation_Request {
  serviceName: string;               // ASMX service name e.g. "ValidatorWrapper,App_Code.ashx"
  methodName: ValidationMethod;      // Method to call, usually "IsExist"
  inactiveErrorMessage?: string;     // True if the field is not required
  strColumnNameForGetValue?: string; // True if the field is not required
  strWhere?: string;                 // strWhere for the IsTableHasRecord method   
}

export enum ValidationMethod {
  IsExist,
  IsExistWhere,
  GetValue,
  IsTableHasRecord
}

export function getValidationMethodName(method: ValidationMethod): string {
  return ValidationMethod[method];
}

export interface IGetValueRequest {
  tableName: string;
  columnName: string;
  where: string;
}

export interface IIsTableHasRecordRequest {
  strTableName: string;
  strWhere: string
}

export interface ITypeAheadIsExistWhereRequest {
  tableName: string;
  columnName: string;
  columnValue: string;
  useQuote: boolean;
  where: string;
}

export interface ITypeaheadControl {
  DisplayText: string; // Text to display in dropdown options
  Value: string;       // Underlying value of the option (id, key, etc.)
}

export const Default_Typeahead = {
  DisplayText: '',
  Value: ''
}
//#endregion
